import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  useTheme,
  IconButton,
  Badge,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  alpha,
  Card,
  CardContent,
  Grid,
  Container,
  Skeleton,
  Fab,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  NotificationsNone as EmptyNotificationsIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  DoneAll as DoneAllIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { ActivityTypes, logActivity } from '../utils/activityLogger';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'viewed'
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const limit = 20;
  const theme = useTheme();

  // Calculate notification stats
  const pendingCount = notifications.filter(n => n.status === 'pending').length;
  const viewedCount = notifications.filter(n => n.status === 'viewed').length;
  const totalCount = notifications.length;

  // Filter notifications by status
  const filteredNotifications = notifications.filter(notification => {
    if (filterStatus === 'all') return true;
    return notification.status === filterStatus;
  });

  // Sort notifications: pending first, then by creation date (newest first)
  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    // First, sort by status: pending comes before viewed
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;

    // Then sort by creation date (newest first)
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  useEffect(() => {
    fetchNotifications(true);
    // Log page visit
    logActivity({
      action: ActivityTypes.NOTIFICATION_VIEW,
      details: 'Viewed notifications page',
      metadata: {
        page: 'notifications',
        action: 'page_view'
      }
    });
  }, []);

  const fetchNotifications = async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }
      setError('');

      const currentPage = isInitial ? 1 : page;
      const response = await api.notifications.getNotifications(currentPage, limit);
      const newNotifications = response.data || [];

      if (isInitial) {
        setNotifications(newNotifications);
      } else {
        setNotifications(prev => [...prev, ...newNotifications]);
      }

      setHasMore(newNotifications.length === limit);
    } catch (err) {
      setError(err.message || 'Error fetching notifications');
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    // We use useEffect to trigger fetch or call it directly if we handle state carefully
    // To make it simple, let's just fetch directly with the next page
    fetchNextPage(nextPage);
  };

  const fetchNextPage = async (nextPage) => {
    try {
      setLoadingMore(true);
      const response = await api.notifications.getNotifications(nextPage, limit);
      const newNotifications = response.data || [];

      setNotifications(prev => [...prev, ...newNotifications]);
      setHasMore(newNotifications.length === limit);
    } catch (err) {
      toast.error('Failed to load more notifications');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchNotifications();
      toast.success('Notifications refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh notifications');
    } finally {
      setRefreshing(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (notification.status === 'pending') {
        await api.notifications.markAsViewed(notification._id);
        // Update local state
        setNotifications(prevNotifications =>
          prevNotifications.map(n =>
            n._id === notification._id ? { ...n, status: 'viewed' } : n
          )
        );
        // Log activity
        await logActivity({
          action: ActivityTypes.NOTIFICATION_MARK_VIEWED,
          details: `Marked notification as viewed: ${notification.title}`,
          metadata: {
            notificationId: notification._id,
            notificationTitle: notification.title
          }
        });
      }
      setSelectedNotification(notification);
      setDialogOpen(true);
      // Log activity for viewing notification details
      await logActivity({
        action: ActivityTypes.NOTIFICATION_VIEW,
        details: `Viewed notification details: ${notification.title}`,
        metadata: {
          notificationId: notification._id,
          notificationTitle: notification.title,
          notificationType: notification.type
        }
      });
    } catch (error) {
      console.error('Error marking notification as viewed:', error);
      toast.error('Failed to mark notification as viewed');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedNotification(null);
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAllAsRead(true);
      await api.notifications.markAllAsViewed();

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(n => ({ ...n, status: 'viewed' }))
      );

      toast.success('All notifications marked as read');

      // Log activity
      await logActivity({
        action: ActivityTypes.NOTIFICATION_MARK_VIEWED,
        details: 'Marked all notifications as viewed',
        metadata: {
          count: pendingCount,
          action: 'mark_all_read'
        }
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return theme.palette.error.main;
      case 'viewed':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'transfer':
        return <PaymentIcon />;
      case 'security':
        return <SecurityIcon />;
      case 'user':
        return <PersonIcon />;
      case 'trending':
        return <TrendingUpIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'transfer':
        return theme.palette.primary.main;
      case 'security':
        return theme.palette.error.main;
      case 'user':
        return theme.palette.success.main;
      case 'trending':
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Skeleton variant="text" width={300} height={60} sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[1, 2, 3].map((item) => (
              <Grid item xs={12} md={4} key={item}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
          {[1, 2, 3, 4, 5].map((item) => (
            <Skeleton key={item} variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 1 }} />
          ))}
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': { fontSize: '1.1rem' }
            }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              Notifications Center
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              {pendingCount > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={markingAllAsRead ? <LoadingSpinner size="small" /> : <DoneAllIcon />}
                  onClick={handleMarkAllAsRead}
                  disabled={markingAllAsRead || pendingCount === 0}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {markingAllAsRead ? 'Marking...' : `Mark All as Read (${pendingCount})`}
                </Button>
              )}
              <Tooltip title="Refresh Notifications">
                <IconButton
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filter Tabs */}
          <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<FilterListIcon />}
              label={`All (${totalCount})`}
              onClick={() => setFilterStatus('all')}
              color={filterStatus === 'all' ? 'primary' : 'default'}
              variant={filterStatus === 'all' ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
                transition: 'all 0.2s ease-in-out',
              }}
            />
            <Chip
              icon={<NotificationsIcon />}
              label={`Pending (${pendingCount})`}
              onClick={() => setFilterStatus('pending')}
              color={filterStatus === 'pending' ? 'error' : 'default'}
              variant={filterStatus === 'pending' ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                },
                transition: 'all 0.2s ease-in-out',
              }}
            />
            <Chip
              icon={<CheckCircleIcon />}
              label={`Read (${viewedCount})`}
              onClick={() => setFilterStatus('viewed')}
              color={filterStatus === 'viewed' ? 'success' : 'default'}
              variant={filterStatus === 'viewed' ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                },
                transition: 'all 0.2s ease-in-out',
              }}
            />
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                  color: 'white',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <NotificationsIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {totalCount}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Notifications
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${alpha(theme.palette.error.main, 0.8)} 100%)`,
                  color: 'white',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.error.main, 0.3)}`,
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Badge badgeContent={pendingCount} color="warning" sx={{ mb: 1 }}>
                    <NotificationsIcon sx={{ fontSize: 40 }} />
                  </Badge>
                  <Typography variant="h4" fontWeight={700}>
                    {pendingCount}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${alpha(theme.palette.success.main, 0.8)} 100%)`,
                  color: 'white',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.3)}`,
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <NotificationsIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {viewedCount}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Viewed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${alpha(theme.palette.info.main, 0.8)} 100%)`,
                  color: 'white',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.info.main, 0.3)}`,
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {totalCount > 0 ? Math.round((viewedCount / totalCount) * 100) : 0}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Read Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Notifications List */}
        <Paper
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          }}
        >
          {sortedNotifications.length > 0 ? (
            <List sx={{ p: 0 }}>
              {sortedNotifications.map((notification, index) => (
                <React.Fragment key={notification._id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      bgcolor: notification.status === 'pending'
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        transform: 'translateX(4px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                      py: 2,
                      px: 3,
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        color={notification.status === 'pending' ? 'error' : 'default'}
                        variant="dot"
                        overlap="circular"
                      >
                        <Avatar
                          sx={{
                            bgcolor: getNotificationColor(notification.type),
                            boxShadow: `0 4px 12px ${alpha(getNotificationColor(notification.type), 0.3)}`,
                          }}
                        >
                          {getNotificationIcon(notification.type)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                          <Typography variant="h6" fontWeight={600} color="text.primary">
                            {notification.title}
                          </Typography>
                          <Chip
                            label={notification.status}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(notification.status),
                              color: 'white',
                              fontWeight: 600,
                              borderRadius: 1,
                              '& .MuiChip-label': { px: 1.5 },
                            }}
                          />
                          {notification.amount && (
                            <Chip
                              label={`₦${notification.amount.toLocaleString()}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 1,
                              lineHeight: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {notification.content}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
                            <Typography variant="caption" color="text.disabled" fontWeight={500}>
                              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                            </Typography>
                          </Box>
                          {notification.user_id && (
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                              <PersonIcon sx={{ fontSize: 16, color: theme.palette.primary.main, opacity: 0.7 }} />
                              <Typography variant="caption" color="primary" fontWeight={600}>
                                Assigned to: {notification.user_id.fullname || notification.user_id.username || 'Unknown User'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < sortedNotifications.length - 1 && (
                    <Divider sx={{ mx: 3, opacity: 0.6 }} />
                  )}
                </React.Fragment>
              ))}
              {hasMore && (
                <Box sx={{ p: 3, textAlign: 'center', borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {loadingMore ? 'Loading More...' : 'Show More Notifications'}
                  </Button>
                </Box>
              )}
            </List>
          ) : (
            /* Impressive Empty State */
            <Box
              sx={{
                py: 8,
                px: 4,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 3,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                      '50%': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                      },
                      '100%': {
                        transform: 'scale(1)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                    },
                  }}
                >
                  <EmptyNotificationsIcon sx={{ fontSize: 60, color: 'white' }} />
                </Box>

                <Typography
                  variant="h4"
                  fontWeight={700}
                  color="text.primary"
                  sx={{ mb: 2 }}
                >
                  {notifications.length === 0
                    ? "All Caught Up! 🎉"
                    : filterStatus === 'pending'
                      ? "No Pending Notifications ✨"
                      : filterStatus === 'viewed'
                        ? "No Read Notifications 📭"
                        : "All Caught Up! 🎉"}
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: 4,
                    maxWidth: 500,
                    mx: 'auto',
                    lineHeight: 1.6,
                  }}
                >
                  {notifications.length === 0
                    ? "You're all up to date! No new notifications at the moment. We'll notify you when there's something important to see."
                    : filterStatus === 'pending'
                      ? "Great! All notifications have been read. Switch to 'All' or 'Read' to see your notification history."
                      : filterStatus === 'viewed'
                        ? "No read notifications found. Switch to 'All' or 'Pending' to see other notifications."
                        : "You're all up to date! No new notifications at the moment."}
                </Typography>

                {notifications.length > 0 && filterStatus !== 'all' && (
                  <Button
                    variant="outlined"
                    onClick={() => setFilterStatus('all')}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    View All Notifications
                  </Button>
                )}

                <Button
                  variant="contained"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  startIcon={<RefreshIcon />}
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {refreshing ? 'Refreshing...' : 'Check for Updates'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="refresh"
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <RefreshIcon />
        </Fab>
      </Box>

      {/* Notification Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.3)}`,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 100%)`
              : `linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
            borderRadius: '16px 16px 0 0',
            p: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={3} sx={{ position: 'relative', zIndex: 1 }}>
            <Avatar
              sx={{
                width: 60,
                height: 60,
                bgcolor: 'rgba(255,255,255,0.2)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                border: '2px solid rgba(255,255,255,0.3)',
                '& .MuiSvgIcon-root': {
                  fontSize: 28,
                }
              }}
            >
              {selectedNotification && getNotificationIcon(selectedNotification.type)}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                Notification Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                {selectedNotification && formatDistanceToNow(new Date(selectedNotification.createdAt), { addSuffix: true })}
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4, background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff' }}>
          {selectedNotification && (
            <Box>
              {/* Main Content */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  gutterBottom
                  color="text.primary"
                  sx={{
                    mb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {selectedNotification.title}
                </Typography>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(145deg, #2d2d2d 0%, #1f1f1f 100%)`
                      : `linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.05)}`,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.8,
                      color: 'text.primary',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                    }}
                  >
                    {selectedNotification.content}
                  </Typography>
                </Box>
              </Box>

              {/* Amount Section */}
              {selectedNotification.amount && (
                <Box
                  sx={{
                    p: 4,
                    mb: 4,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.main, 0.08)} 100%)`,
                    border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.15)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)} 0%, transparent 100%)`,
                    }
                  }}
                >
                  <Typography variant="h6" color="success.main" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                    💰 Transaction Amount
                  </Typography>
                  <Typography variant="h3" fontWeight={900} color="success.main" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    ₦{selectedNotification.amount.toLocaleString()}
                  </Typography>
                </Box>
              )}

              {/* Transaction Details */}
              {selectedNotification.transaction_details && (
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(145deg, #2d2d2d 0%, #1f1f1f 100%)`
                      : `linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)`,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
                    mb: 4,
                  }}
                >
                  <Typography variant="h6" color="primary" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
                    📋 Transaction Details
                  </Typography>
                  <Grid container spacing={3}>
                    {Object.entries(selectedNotification.transaction_details).map(([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.05)}`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
                            }
                          }}
                        >
                          <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ mt: 1, color: 'text.primary' }}>
                            {typeof value === 'object' && value !== null
                              ? `${value.name || value.id} ${value.username ? `(${value.username})` : ''}`
                              : typeof value === 'number'
                                ? `₦${value.toLocaleString()}`
                                : String(value)}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Footer Info */}
              <Box
                display="flex"
                alignItems="center"
                gap={3}
                p={3}
                sx={{
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(145deg, #2d2d2d 0%, #1f1f1f 100%)`
                    : `linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.05)}`,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AccessTimeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                      Created
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {formatDistanceToNow(new Date(selectedNotification.createdAt), { addSuffix: true })}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                  <Chip
                    label={selectedNotification.status}
                    size="medium"
                    sx={{
                      bgcolor: getStatusColor(selectedNotification.status),
                      color: 'white',
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontSize: '0.9rem',
                      boxShadow: `0 4px 12px ${alpha(getStatusColor(selectedNotification.status), 0.3)}`,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 0, background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff' }}>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notifications; 